// Global Instructions Rule Applied!

import { startTransition, useCallback, useEffect, useRef, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  BulkAccount,
  BulkTopic,
  BulkFilterOptionsModel
} from '../../../state_manager/data_models/bulk_account_topics_grid_model.ts'
import {
  BulkAccountTopicsFilters,
  BulkAccountTopicsSavePayload
} from '../../../state_manager/interfaces/distribution_lists.ts'
import {
  fetchBulkAccountTopics,
  fetchBulkFilterOptions,
  saveBulkAccountTopics
} from '../services/distributionListsService.ts'

// Smaller page → less DOM built per infinite-scroll append (the table is an
// unvirtualized AntD tree-Table, §2.14b); combined with the 200px sentinel
// prefetch + the non-blocking startTransition append commit, scrolling stays
// smooth without reverting the §2.11d infinite-scroll decision.
const PAGE_SIZE = 30

const EMPTY_FILTERS: BulkAccountTopicsFilters = {
  role_ids: [],
  fund_ids: [],
  lp_status_ids: [],
  search: ''
}

/**
 * useTopicSubscriptions — the Topic Subscriptions screen's own data RPC (not
 * the cached globalConfigs copy): a post-write refetch must reflect DB truth
 * incl. the Phase 0 propagate-trigger cascade without a backend restart.
 * Screen-local state, no Redux (decision §2.9e), mirroring the Phase 2
 * useAccountTopics hook.
 *
 * Infinite scroll (architect UI review 2026-05-17, replaces pagination):
 * pages ACCUMULATE — load() appends the next page's accounts rather than
 * replacing, so editing spans the whole scrolled set with no
 * page-change data loss. applyFilters / refetch reset to page 1; the
 * post-save refetch resets to page 1 (save cleared the dirty edits).
 */
export const useTopicSubscriptions = () => {
  const [filters, setFilters] = useState<BulkAccountTopicsFilters>(EMPTY_FILTERS)

  const [accounts, setAccounts] = useState<BulkAccount[]>([])
  // Bumped on every RESET fetch (initial / applyFilters / refetch /
  // post-save) and NOT on an infinite-scroll append. The screen uses it to
  // decide whether to reseed all selection state from fresh DB truth (reset
  // — e.g. clears the dirty flag after Save) or preserve in-progress edits
  // for already-loaded accounts (append).
  const [resetToken, setResetToken] = useState(0)
  const [topics, setTopics] = useState<BulkTopic[]>([])
  const [count, setCount] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [options, setOptions] = useState<BulkFilterOptionsModel | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const cancelledRef = useRef(false)
  useEffect(() => {
    cancelledRef.current = false
    return () => {
      cancelledRef.current = true
    }
  }, [])

  const activeFiltersRef = useRef<BulkAccountTopicsFilters>(EMPTY_FILTERS)
  const pageRef = useRef(1)
  // Guards against overlapping fetches (scroll can fire loadMore rapidly).
  const inFlightRef = useRef(false)

  const fetchPage = useCallback(async (page: number, reset: boolean) => {
    if (inFlightRef.current) return
    inFlightRef.current = true
    if (reset) setLoading(true)
    else setLoadingMore(true)
    setError(null)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchBulkAccountTopics(activeFiltersRef.current, page, PAGE_SIZE, token)
      if (cancelledRef.current) return
      if (res.error) {
        setError(res.error)
        return
      }
      const payload = res.data
      if (!payload) {
        if (reset) {
          setAccounts([])
          setTopics([])
          setCount(0)
          setHasMore(false)
          setResetToken((t) => t + 1)
        }
        return
      }
      setTopics(payload.comms_topics ?? [])
      setCount(payload.count ?? 0)
      setHasMore(Boolean(payload.has_more))
      pageRef.current = page
      if (reset) {
        setResetToken((t) => t + 1)
        setAccounts(payload.data ?? [])
      } else {
        // Append (infinite scroll). The table is a self-contained AntD
        // tree-Table (§2.14b — no TableView/`virtual`), so a big appended
        // page would otherwise re-render the whole accumulated tree
        // synchronously and freeze the scroll for a beat (the standard
        // grids stay smooth via TableView virtualization, which an
        // expandable tree-Table can't use). Commit the heavy append as a
        // non-urgent transition so React keeps the page responsive and
        // renders the new rows without blocking the frame.
        startTransition(() => {
          setAccounts((prev) => {
            // De-dupe by account id (defensive against scroll overlap).
            const seen = new Set(prev.map((a) => a.accounts__id))
            const next = [...prev]
            for (const a of payload.data ?? []) {
              if (!seen.has(a.accounts__id)) next.push(a)
            }
            return next
          })
        })
      }
    } catch (e) {
      if (!cancelledRef.current) {
        setError(e instanceof Error ? e.message : 'Failed to load topic subscriptions')
      }
    } finally {
      if (!cancelledRef.current) {
        setLoading(false)
        setLoadingMore(false)
      }
      inFlightRef.current = false
    }
  }, [])

  // Filter-options once on mount (its own RPC; fetched once — CLAUDE.md).
  useEffect(() => {
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchBulkFilterOptions(token)
      if (cancelledRef.current) return
      if (res.error) setError((prev) => prev ?? res.error ?? null)
      setOptions(res.data ?? null)
    })()
  }, [])

  // Initial page.
  useEffect(() => {
    fetchPage(1, true)
  }, [fetchPage])

  const applyFilters = useCallback(
    (next: BulkAccountTopicsFilters) => {
      setFilters(next)
      activeFiltersRef.current = next
      pageRef.current = 1
      fetchPage(1, true)
    },
    [fetchPage]
  )

  const loadMore = useCallback(() => {
    if (inFlightRef.current || !hasMore) return
    fetchPage(pageRef.current + 1, false)
  }, [fetchPage, hasMore])

  const refetch = useCallback(() => {
    pageRef.current = 1
    fetchPage(1, true)
  }, [fetchPage])

  const save = useCallback(
    async (payload: BulkAccountTopicsSavePayload): Promise<boolean> => {
      if (!payload.accounts || payload.accounts.length === 0) return false
      setSaving(true)
      setError(null)
      try {
        const token = getAccessToken(store.getState().auth)
        const res = await saveBulkAccountTopics(payload, token)
        if (!res.success) {
          if (!cancelledRef.current) setError(res.error ?? 'Failed to save topic subscriptions')
          return false
        }
        // Re-pull authoritative state from page 1 (save cleared dirty + the
        // Phase 0 propagate trigger / contact ⊆ account intersection ran, so
        // the grid must reflect the post-write DB, not optimistic edits).
        pageRef.current = 1
        await fetchPage(1, true)
        return true
      } catch (e) {
        if (!cancelledRef.current) {
          setError(e instanceof Error ? e.message : 'Failed to save topic subscriptions')
        }
        return false
      } finally {
        if (!cancelledRef.current) setSaving(false)
      }
    },
    [fetchPage]
  )

  return {
    filters,
    setFilters,
    applyFilters,
    accounts,
    resetToken,
    topics,
    count,
    hasMore,
    options,
    loading,
    loadingMore,
    saving,
    error,
    loadMore,
    refetch,
    save
  }
}
